/**
 * Pure helpers for presenting the ranked makeup staff recommendations.
 *
 * The authoritative eligibility + `recommendation_score` + `is_best_match` are
 * computed by the backend (makeup-options endpoint). These helpers only DERIVE
 * display text from that structured data — they never re-rank or invent reasons.
 */

export interface GradeSubject {
  grade: string | { name?: string };
  subjects: Array<string | { name?: string }>;
}

/** Total subject count across a [{grade, subjects[]}] mapping (coverage size). */
export const gsCount = (gs?: GradeSubject[] | null): number =>
  Array.isArray(gs) ? gs.reduce((n, g) => n + ((g?.subjects?.length) || 0), 0) : 0;

/**
 * Why a non-best available teacher ranks below the best one — derived ONLY from
 * real signals (broader grade-subject coverage, then original-teacher status).
 * Returns undefined for the best staff itself. Never fabricates a reason.
 */
export const rankNote = (best: any, staff: any): string | undefined => {
  if (!best || !staff || best.id === staff.id) return undefined;
  if (gsCount(best.qualified_grade_subjects) > gsCount(staff.qualified_grade_subjects)) {
    return `${best.name} is ranked higher — qualified for more of this class's grade-subjects.`;
  }
  if (best.is_original && !staff.is_original) {
    return `${best.name} is the class's original teacher.`;
  }
  return `Equally qualified and available; ${best.name} is the recommended top pick.`;
};
